import Phaser from 'phaser';
import { ClassDefinition } from '../data/Classes';
import { SpecialDefinition } from '../data/Specials';
import {
  BEYBLADE_CORE_RADIUS,
  CAT_BEYBLADE,
  PLAYER_COLORS,
  BASE_SPIN_SPEED,
  FRICTION_AIR,
  FRICTION_GROUND,
  RESTITUTION,
  MAX_BEYBLADE_SPEED,
  HP_MULTIPLIER,
} from '../config/Constants';

export interface ActiveEffect {
  id: string;
  name: string;
  remainingMs: number;
  duration: number;
  onUpdate?: (beyblade: Beyblade, delta: number) => void;
  onEnd?: (beyblade: Beyblade) => void;
}

export type PlayerStatus = 'alive' | 'eliminated';

export class Beyblade {
  public playerIndex: number;
  public classDef: ClassDefinition;
  public specialDef: SpecialDefinition | null;
  public scene: Phaser.Scene;
  public body: MatterJS.BodyType;
  public graphics: Phaser.GameObjects.Graphics;

  // Stats actuales (con buffs aplicados)
  public hp: number;
  public maxHp: number;
  public attack: number;
  public speed: number;
  public defense: number;
  public stability: number;

  // Estado
  public status: PlayerStatus = 'alive';
  public points: number = 0;
  public combo: number = 0;
  public timeWithoutHit: number = 0;
  public effects: ActiveEffect[] = [];
  public shieldHits: number = 0; // para especial Escudo Inicial
  public berserkerStacks: number = 0;
  public powerUpCooldown: number = 0;
  public lastHitBy: number = -1;
  public color: number;

  // Flags de especiales
  public hasInitialShield: boolean = false;
  public specialCooldowns: Map<string, number> = new Map();

  // Radio visual
  public readonly visualRadius: number;

  constructor(
    scene: Phaser.Scene,
    playerIndex: number,
    classDef: ClassDefinition,
    specialDef: SpecialDefinition | null,
    x: number,
    y: number
  ) {
    this.scene = scene;
    this.playerIndex = playerIndex;
    this.classDef = classDef;
    this.specialDef = specialDef;
    this.color = PLAYER_COLORS[playerIndex] || 0xffffff;
    this.visualRadius = BEYBLADE_CORE_RADIUS + 18;

    // Inicializar stats base × clase × multiplicador global
    this.maxHp = Math.round(classDef.hp * HP_MULTIPLIER);
    this.hp = this.maxHp;
    this.attack = classDef.attack;
    this.speed = classDef.speed;
    this.defense = classDef.defense;
    this.stability = classDef.stability;

    // Aplicar especial "Más Grande"
    if (specialDef?.id === 'mas_grande') {
      this.visualRadius *= 1.3;
    }

    // Crear cuerpo físico Matter.js
    const radius = this.visualRadius;
    this.body = scene.matter.add.circle(x, y, radius, {
      label: `beyblade_${playerIndex}`,
      collisionFilter: {
        category: CAT_BEYBLADE,
        mask: 0xFFFF,
        group: 0,
      },
      friction: FRICTION_GROUND,
      frictionAir: FRICTION_AIR,
      restitution: RESTITUTION,
      density: classDef.density,
    });

    // Aplicar especial "Más Grande" - masa extra
    if (specialDef?.id === 'mas_grande') {
      const scaleFactor = 1.5;
      const currentMass = (this.body as any).mass || 1;
      scene.matter.body.setMass(this.body, currentMass * scaleFactor);
    }

    // Velocidad angular inicial (siempre empieza girando)
    scene.matter.body.setAngularVelocity(this.body, classDef.speed);

    // Mini impulso lineal inicial aleatorio para que todas se muevan desde el principio
    const initAngle = Math.random() * Math.PI * 2;
    const initForce = 0.00005 + Math.random() * 0.0001;
    scene.matter.body.applyForce(this.body, this.body.position, {
      x: Math.cos(initAngle) * initForce,
      y: Math.sin(initAngle) * initForce,
    });

    // Crear representación visual
    this.graphics = scene.add.graphics();
    this.draw();

    // Inicializar flags de especiales
    if (specialDef?.id === 'escudo_inicial') {
      this.hasInitialShield = true;
    }
  }

  draw(): void {
    this.graphics.clear();
    const x = this.body.position.x;
    const y = this.body.position.y;
    const angle = this.body.angle || 0;
    const r = this.visualRadius;

    // Sombra
    this.graphics.fillStyle(0x000000, 0.3);
    this.graphics.fillCircle(x + 3, y + 3, r);

    // Cuerpo principal (anillo exterior)
    this.graphics.fillStyle(this.color, 1);
    this.graphics.fillCircle(x, y, r);

    // Anillo interior (más oscuro)
    const innerR = r * 0.65;
    this.graphics.fillStyle(Phaser.Display.Color.ValueToColor(this.color).darken(30).color, 1);
    this.graphics.fillCircle(x, y, innerR);

    // Centro (brillante)
    const coreR = r * 0.35;
    this.graphics.fillStyle(Phaser.Display.Color.ValueToColor(this.color).lighten(30).color, 1);
    this.graphics.fillCircle(x, y, coreR);

    // Detalle de giro - líneas radiales
    this.graphics.lineStyle(2, 0xffffff, 0.4);
    for (let i = 0; i < 4; i++) {
      const a = angle + (i * Math.PI) / 2;
      const startR = coreR + 2;
      this.graphics.beginPath();
      this.graphics.moveTo(x + Math.cos(a) * startR, y + Math.sin(a) * startR);
      this.graphics.lineTo(x + Math.cos(a) * innerR, y + Math.sin(a) * innerR);
      this.graphics.strokePath();
    }

    // Número de jugador
    const textX = x - 5;
    const textY = y - 7;
    // Usar texto simple de Phaser
  }

  applyDamage(amount: number, knockback: Phaser.Math.Vector2, attackerIndex: number): boolean {
    // Escudo inicial
    if (this.hasInitialShield) {
      this.hasInitialShield = false;
      return false; // daño bloqueado
    }

    // Escudo de power-up
    const shieldEffect = this.effects.find((e) => e.id === 'escudo');
    if (shieldEffect) {
      return false;
    }

    // Calcular daño real
    const defenseMultiplier = 1 / this.defense;
    const finalDamage = Math.max(1, Math.round(amount * defenseMultiplier));

    this.hp = Math.max(0, this.hp - finalDamage);

    // Berserker: acumula stacks
    if (this.specialDef?.id === 'berserker' && this.berserkerStacks < 10) {
      this.berserkerStacks++;
      this.attack *= 1.05;
    }

    // Giro explosivo
    if (this.specialDef?.id === 'giro_explosivo') {
      const cdKey = 'giro_explosivo';
      const now = Date.now();
      const lastUse = this.specialCooldowns.get(cdKey) || 0;
      if (now - lastUse >= 5000) {
        this.specialCooldowns.set(cdKey, now);
        // Liberar onda expansiva (se maneja desde BattleSystem)
        this.lastHitBy = attackerIndex;
      }
    }

    // Knockback
    const kbMultiplier = 1 / this.defense;
    const finalKbX = knockback.x * kbMultiplier;
    const finalKbY = knockback.y * kbMultiplier;

    this.scene.matter.body.applyForce(this.body, this.body.position, {
      x: finalKbX,
      y: finalKbY,
    });

    this.lastHitBy = attackerIndex;
    this.timeWithoutHit = 0;

    // Vampiro: curar al atacante si él tiene vampiro
    // Se maneja desde BattleSystem

    return this.hp <= 0;
  }

  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }

  applyKnockback(force: Phaser.Math.Vector2): void {
    // Especial Roca: 90% reducción
    if (this.specialDef?.id === 'roca') {
      force.x *= 0.1;
      force.y *= 0.1;
    }

    // Especial Elástico: se maneja externamente
    MatterJS.Body.applyForce(this.body as any, this.body.position, {
      x: force.x,
      y: force.y,
    });
  }

  isAlive(): boolean {
    return this.status === 'alive';
  }

  destroy(): void {
    this.graphics.destroy();
    const world = this.scene.matter.world.localWorld;
    (this.scene.matter.composite as any).remove(world, this.body);
  }

  update(delta: number): void {
    if (this.status !== 'alive') return;

    // Actualizar efectos temporales
    for (let i = this.effects.length - 1; i >= 0; i--) {
      this.effects[i].remainingMs -= delta;
      if (this.effects[i].remainingMs <= 0) {
        if (this.effects[i].onEnd) {
          this.effects[i].onEnd!(this);
        }
        this.effects.splice(i, 1);
      } else {
        if (this.effects[i].onUpdate) {
          this.effects[i].onUpdate!(this, delta);
        }
      }
    }

    // Power-up cooldown
    if (this.powerUpCooldown > 0) {
      this.powerUpCooldown -= delta;
    }

    // Combo: reset si pasa mucho tiempo sin golpear
    this.timeWithoutHit += delta;
    if (this.timeWithoutHit > 3000) {
      this.combo = 0;
    }

    // Actualizar posición visual
    this.draw();

    // Limitar velocidad lineal máxima
    const vel = this.body.velocity;
    const currentSpeed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
    if (currentSpeed > MAX_BEYBLADE_SPEED) {
      const scale = MAX_BEYBLADE_SPEED / currentSpeed;
      this.scene.matter.body.setVelocity(this.body, { x: vel.x * scale, y: vel.y * scale });
    }

    // Mantener velocidad angular
    const spinSpeed = this.speed;
    this.scene.matter.body.setAngularVelocity(this.body, spinSpeed);
  }
}
